/* ==========================================================================
   HOMY — JavaScript (vanilla, custom elements)
   ========================================================================== */
(() => {
  'use strict';

  /* ------------------------------------------------------------------
     Utilitaires
     ------------------------------------------------------------------ */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const H = window.homy || { routes: {}, strings: {}, settings: {} };

  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(null, args), wait); };
  };

  const jsonHeaders = () => ({ 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' });

  const formatMoney = (cents, format = H.settings.moneyFormat || '{{amount_with_comma_separator}} €') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholderRegex);
    const type = match ? match[1] : 'amount';
    const num = (n, precision, thousands, decimal) => {
      n = (n / 100).toFixed(precision);
      const parts = n.split('.');
      const int = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] && precision > 0 ? `${int}${decimal}${parts[1]}` : int;
    };
    let value = '';
    switch (type) {
      case 'amount': value = num(cents, 2, ',', '.'); break;
      case 'amount_no_decimals': value = num(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = num(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = num(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = num(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = num(cents, 0, ' ', ','); break;
      case 'amount_with_apostrophe_separator': value = num(cents, 2, "'", '.'); break;
      default: value = num(cents, 2, ',', '.');
    }
    // Les prix ronds s'affichent sans décimales (299 € plutôt que 299,00 €)
    value = value.replace(/([,.])00$/, '');
    return format.replace(placeholderRegex, value);
  };

  const toast = (message, type = '') => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.className = `toast ${type ? `toast--${type}` : ''}`;
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.classList.remove('is-visible'); setTimeout(() => { el.hidden = true; }, 300); }, 2600);
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const focusable = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const trapFocus = (container) => {
    const els = $$(focusable, container).filter((el) => el.offsetParent !== null);
    if (!els.length) return () => {};
    const first = els[0]; const last = els[els.length - 1];
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    setTimeout(() => (($('[autofocus]', container) || first).focus()), 60);
    return () => container.removeEventListener('keydown', handler);
  };

  /* ------------------------------------------------------------------
     Overlay global (une seule couche sombre pour tous les panneaux)
     ------------------------------------------------------------------ */
  const Overlay = {
    el: null, owners: new Set(),
    init() { this.el = $('[data-overlay]'); this.el?.addEventListener('click', () => this.owners.forEach((o) => o.close())); },
    show(owner) { this.owners.add(owner); if (!this.el) return; this.el.hidden = false; requestAnimationFrame(() => this.el.classList.add('is-visible')); document.body.classList.add('is-locked'); },
    hide(owner) { this.owners.delete(owner); if (this.owners.size || !this.el) return; this.el.classList.remove('is-visible'); document.body.classList.remove('is-locked'); setTimeout(() => { if (!this.owners.size) this.el.hidden = true; }, 300); }
  };

  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') Overlay.owners.forEach((o) => o.close()); });

  /* ------------------------------------------------------------------
     Drawer générique
     <homy-drawer id="MenuDrawer"> … <button data-open-drawer="MenuDrawer">
     ------------------------------------------------------------------ */
  class HomyDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer, .facets, .modal, .search-overlay') || this;
      this.querySelectorAll('[data-close-drawer]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.setAttribute('aria-hidden', 'true');
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.panel.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      Overlay.show(this);
      this.releaseFocus = trapFocus(this.panel);
      this.dispatchEvent(new CustomEvent('drawer:open'));
    }
    close() {
      if (!this.panel.classList.contains('is-open')) return;
      this.panel.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      Overlay.hide(this);
      this.releaseFocus?.();
      this.opener?.focus?.();
      this.dispatchEvent(new CustomEvent('drawer:close'));
    }
    get isOpen() { return this.panel.classList.contains('is-open'); }
  }
  customElements.define('homy-drawer', HomyDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-drawer]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.openDrawer);
    if (drawer?.open) { e.preventDefault(); drawer.open(opener); }
  });

  /* ------------------------------------------------------------------
     Header : ombre + rétrécissement au scroll, mega-menu
     ------------------------------------------------------------------ */
  class HomyHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.header-wrapper') || this;
      this.onScroll = () => this.wrapper.classList.toggle('is-scrolled', window.scrollY > 24);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.updateStickyTop();
      window.addEventListener('resize', debounce(() => this.updateStickyTop(), 150));

      // Mega menu : hover avec léger délai, focus clavier, clic sur tactile
      this.querySelectorAll('.header__nav-item.has-mega').forEach((item) => {
        let t;
        const link = item.querySelector('.header__nav-link');
        const open = () => { clearTimeout(t); this.closeAll(item); item.classList.add('is-open'); link.setAttribute('aria-expanded', 'true'); };
        const close = () => { t = setTimeout(() => { item.classList.remove('is-open'); link.setAttribute('aria-expanded', 'false'); }, 120); };
        item.addEventListener('mouseenter', open);
        item.addEventListener('mouseleave', close);
        item.addEventListener('focusin', open);
        item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) close(); });
        link.addEventListener('click', (e) => {
          if (window.matchMedia('(hover: none)').matches && !item.classList.contains('is-open')) { e.preventDefault(); open(); }
        });
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeAll(); });
    }
    closeAll(except) { this.querySelectorAll('.header__nav-item.is-open').forEach((i) => { if (i !== except) { i.classList.remove('is-open'); i.querySelector('.header__nav-link')?.setAttribute('aria-expanded', 'false'); } }); }
    updateStickyTop() {
      const h = this.wrapper.offsetHeight;
      document.documentElement.style.setProperty('--sticky-top', `${h}px`);
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
  }
  customElements.define('homy-header', HomyHeader);

  /* ------------------------------------------------------------------
     Panier : drawer + mise à jour par Section Rendering API
     ------------------------------------------------------------------ */
  const Cart = {
    sectionId: 'cart-drawer',
    async add(body) {
      const payload = { ...body, sections: this.sectionId, sections_url: window.location.pathname };
      const res = await fetch(H.routes.cartAdd || '/cart/add.js', { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || H.strings.error);
      this.render(data.sections?.[this.sectionId]);
      return data;
    },
    async change(body) {
      const payload = { ...body, sections: this.sectionId, sections_url: window.location.pathname };
      const res = await fetch(H.routes.cartChange || '/cart/change.js', { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || H.strings.cartError);
      this.render(data.sections?.[this.sectionId]);
      return data;
    },
    async refresh() {
      const res = await fetch(`${window.location.pathname}?sections=${this.sectionId}`);
      const data = await res.json();
      this.render(data[this.sectionId]);
    },
    render(html) {
      if (!html) return;
      const doc = parseHTML(html);
      const fresh = doc.querySelector('cart-drawer');
      const current = document.querySelector('cart-drawer');
      if (fresh && current) {
        const wasOpen = current.isOpen;
        current.querySelector('[data-cart-drawer-content]').innerHTML = fresh.querySelector('[data-cart-drawer-content]').innerHTML;
        if (wasOpen) current.panel.classList.add('is-open');
      }
      const count = fresh?.dataset.cartCount ?? doc.querySelector('[data-cart-count]')?.dataset.cartCount;
      if (count !== undefined) this.updateCount(count);
      // Page panier
      const freshPage = doc.querySelector('[data-cart-page-items]');
      const currentPage = document.querySelector('[data-cart-page-items]');
      if (freshPage && currentPage) currentPage.innerHTML = freshPage.innerHTML;
      document.dispatchEvent(new CustomEvent('cart:updated'));
    },
    updateCount(count) {
      $$('.cart-count').forEach((el) => {
        el.textContent = count;
        el.dataset.count = count;
        el.classList.remove('is-bumping');
        void el.offsetWidth;
        el.classList.add('is-bumping');
      });
    },
    open() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && H.settings.cartType === 'drawer') drawer.open();
      else window.location.href = H.routes.cart || '/cart';
    }
  };

  class CartDrawer extends HomyDrawer {
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) { e.preventDefault(); this.changeItem(remove.closest('[data-line]'), 0); }
      });
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cart-quantity]');
        if (input) this.changeItem(input.closest('[data-line]'), parseInt(input.value, 10));
      });
    }
    async changeItem(row, quantity) {
      if (!row) return;
      row.classList.add('is-removing');
      try { await Cart.change({ line: row.dataset.line, quantity }); }
      catch (err) { row.classList.remove('is-removing'); toast(err.message, 'error'); }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  // Cross-sell dans le panier : bouton d'ajout direct
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cross-sell-add]');
    if (!btn) return;
    e.preventDefault();
    btn.classList.add('is-loading');
    try { await Cart.add({ items: [{ id: btn.dataset.crossSellAdd, quantity: 1 }] }); }
    catch (err) { toast(err.message, 'error'); }
    finally { btn.classList.remove('is-loading'); }
  });

  /* ------------------------------------------------------------------
     Formulaire produit (fiche produit + sticky)
     ------------------------------------------------------------------ */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[type="submit"]');
      this.form?.addEventListener('submit', (e) => this.submit(e));
    }
    async submit(e) {
      e.preventDefault();
      if (this.button?.hasAttribute('disabled')) return;
      const fd = new FormData(this.form);
      const id = fd.get('id');
      const quantity = parseInt(fd.get('quantity') || '1', 10);
      const properties = {};
      fd.forEach((v, k) => { const m = k.match(/^properties\[(.+)\]$/); if (m) properties[m[1]] = v; });
      this.button.classList.add('is-loading');
      try {
        await Cart.add({ items: [{ id, quantity, properties }] });
        this.button.classList.remove('is-loading');
        this.button.classList.add('is-success');
        const label = this.button.querySelector('span') || this.button;
        const original = label.textContent;
        label.textContent = H.strings.added;
        setTimeout(() => { this.button.classList.remove('is-success'); label.textContent = original; }, 1800);
        Cart.open();
      } catch (err) {
        this.button.classList.remove('is-loading');
        toast(err.message, 'error');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ------------------------------------------------------------------
     Quick add (card produit)
     ------------------------------------------------------------------ */
  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-quick-add-button]');
      this.popoverEl = this.querySelector('[data-quick-add-popover]');
      this.button?.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = this.button.dataset.variantId;
        if (variantId) this.add(variantId, this.button);
        else this.togglePopover(true);
      });
      this.popoverEl?.querySelector('[data-quick-add-close]')?.addEventListener('click', () => this.togglePopover(false));
      this.popoverEl?.querySelectorAll('[data-variant-id]').forEach((pill) => {
        pill.addEventListener('click', (e) => { e.preventDefault(); this.add(pill.dataset.variantId, pill); });
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.togglePopover(false); });
    }
    togglePopover(show) {
      if (!this.popoverEl) return;
      this.popoverEl.hidden = !show;
      if (show) trapFocus(this.popoverEl);
    }
    async add(id, trigger) {
      this.button.classList.add('is-loading');
      trigger?.classList.add('is-loading');
      try {
        await Cart.add({ items: [{ id, quantity: 1 }] });
        this.button.classList.remove('is-loading');
        this.button.classList.add('is-success');
        setTimeout(() => this.button.classList.remove('is-success'), 1500);
        this.togglePopover(false);
        Cart.open();
      } catch (err) {
        this.button.classList.remove('is-loading');
        toast(err.message, 'error');
      } finally { trigger?.classList.remove('is-loading'); }
    }
  }
  customElements.define('quick-add', QuickAdd);

  /* ------------------------------------------------------------------
     Sélecteur de variantes (fiche produit)
     ------------------------------------------------------------------ */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants-json]')?.textContent || '[]');
      this.productEl = this.closest('[data-product]');
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }
    get selectedOptions() {
      return $$('fieldset', this).map((fs) => fs.querySelector('input:checked')?.value ?? fs.querySelector('select')?.value);
    }
    findVariant() {
      const opts = this.selectedOptions;
      return this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    }
    onChange() {
      const variant = this.findVariant();
      this.updateAvailability();
      this.querySelectorAll('[data-option-value]').forEach((el) => {
        const fs = el.closest('fieldset');
        const chosen = fs.querySelector('input:checked')?.value;
        if (chosen) el.textContent = chosen;
      });
      if (!this.productEl) return;
      const btn = this.productEl.querySelector('[data-add-to-cart]');
      const btnLabel = btn?.querySelector('span') || btn;
      const idInputs = this.productEl.querySelectorAll('input[name="id"]');
      const priceEls = this.productEl.querySelectorAll('[data-product-price]');
      const stickyImg = this.productEl.querySelector('[data-sticky-image]');
      if (!variant) {
        btn?.setAttribute('disabled', '');
        if (btnLabel) btnLabel.textContent = H.strings.unavailable;
        return;
      }
      idInputs.forEach((i) => { i.value = variant.id; });
      priceEls.forEach((el) => {
        el.classList.toggle('price--sale', variant.compare_at_price > variant.price);
        const cur = el.querySelector('.price__current'); const cmp = el.querySelector('.price__compare');
        if (cur) cur.textContent = formatMoney(variant.price);
        if (cmp) { cmp.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : ''; cmp.hidden = !(variant.compare_at_price > variant.price); }
      });
      const saleBadge = this.productEl.querySelector('[data-sale-badge]');
      if (saleBadge) {
        if (variant.compare_at_price > variant.price) { saleBadge.hidden = false; saleBadge.textContent = `-${Math.round((1 - variant.price / variant.compare_at_price) * 100)} %`; }
        else saleBadge.hidden = true;
      }
      if (btn) {
        if (variant.available) { btn.removeAttribute('disabled'); if (btnLabel) btnLabel.textContent = H.strings.addToCart; }
        else { btn.setAttribute('disabled', ''); if (btnLabel) btnLabel.textContent = H.strings.soldOut; }
      }
      const stock = this.productEl.querySelector('[data-stock-status]');
      if (stock) {
        stock.className = `stock ${variant.available ? '' : 'stock--out'}`;
        stock.textContent = variant.available ? (stock.dataset.inStock || '') : H.strings.soldOut;
      }
      const sku = this.productEl.querySelector('[data-sku]');
      if (sku) { sku.textContent = variant.sku || ''; sku.closest('[data-sku-wrap]')?.toggleAttribute('hidden', !variant.sku); }
      if (variant.featured_media?.id) {
        this.productEl.querySelector('product-gallery')?.goToMedia(variant.featured_media.id);
        if (stickyImg && variant.featured_media.preview_image?.src) stickyImg.src = variant.featured_media.preview_image.src.replace(/(\.[a-z]+)(\?.*)?$/i, '_120x$1$2');
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
    updateAvailability() {
      // Marque en pointillés les valeurs qui ne mènent à aucune variante disponible avec le reste de la sélection
      const fieldsets = $$('fieldset', this);
      fieldsets.forEach((fs, idx) => {
        const others = this.selectedOptions;
        fs.querySelectorAll('input').forEach((input) => {
          const trial = [...others]; trial[idx] = input.value;
          const match = this.variants.find((v) => v.options.every((o, i) => i === idx ? o === input.value : (o === trial[i])));
          const label = input.nextElementSibling;
          label?.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------------
     Galerie produit
     ------------------------------------------------------------------ */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = $$('[data-media-id]', this.track);
      this.thumbs = $$('[data-thumb]', this);
      this.dots = $$('[data-dot]', this);
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(parseInt(t.dataset.thumb, 10))));
      this.dots.forEach((d) => d.addEventListener('click', () => this.goTo(parseInt(d.dataset.dot, 10))));
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
      this.querySelector('[data-gallery-zoom]')?.addEventListener('click', () => this.zoom());
      this.track?.addEventListener('scroll', debounce(() => this.syncFromScroll(), 60), { passive: true });
      this.index = 0;
      this.setCurrent(0);
    }
    goTo(i) {
      if (!this.slides.length) return;
      i = Math.max(0, Math.min(this.slides.length - 1, i));
      this.track.scrollTo({ left: this.slides[i].offsetLeft, behavior: 'smooth' });
      this.setCurrent(i);
    }
    goToMedia(id) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i >= 0) this.goTo(i);
    }
    syncFromScroll() {
      const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (i !== this.index) this.setCurrent(i);
    }
    setCurrent(i) {
      this.index = i;
      this.thumbs.forEach((t, k) => t.setAttribute('aria-current', k === i ? 'true' : 'false'));
      this.dots.forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
      const thumb = this.thumbs[i];
      thumb?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }
    zoom() {
      const img = this.slides[this.index]?.querySelector('img');
      if (!img) return;
      const src = img.dataset.zoom || img.currentSrc || img.src;
      let modal = $('#ZoomModal');
      if (!modal) {
        modal = document.createElement('homy-drawer');
        modal.id = 'ZoomModal';
        modal.innerHTML = `<div class="modal modal--zoom" role="dialog" aria-modal="true"><button class="modal__close" type="button" data-close-drawer aria-label="${H.strings.close || 'Fermer'}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button><div class="modal__panel"><img alt=""></div></div>`;
        document.body.appendChild(modal);
      }
      modal.querySelector('img').src = src;
      modal.open();
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ------------------------------------------------------------------
     Sticky add-to-cart (mobile)
     ------------------------------------------------------------------ */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.querySelector('[data-add-to-cart]');
      if (!target || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0 });
      io.observe(target);
      this.querySelector('button')?.addEventListener('click', () => {
        const form = document.querySelector('product-form form');
        form?.requestSubmit?.() || form?.dispatchEvent(new Event('submit', { cancelable: true }));
      });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ------------------------------------------------------------------
     Quantité
     ------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault();
        const step = b.dataset.step === 'down' ? -1 : 1;
        const min = parseInt(this.input.min || '1', 10);
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + step));
        if (next !== parseInt(this.input.value, 10)) { this.input.value = next; this.input.dispatchEvent(new Event('change', { bubbles: true })); }
      }));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------
     Recherche prédictive
     ------------------------------------------------------------------ */
  class PredictiveSearch extends HomyDrawer {
    connectedCallback() {
      super.connectedCallback();
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.defaults = this.querySelector('[data-search-default]');
      this.form = this.querySelector('form');
      this.input?.addEventListener('input', debounce(() => this.search(), 220));
      this.querySelector('[data-search-reset]')?.addEventListener('click', () => { this.input.value = ''; this.reset(); this.input.focus(); });
      this.addEventListener('drawer:open', () => setTimeout(() => this.input?.focus(), 80));
      this.querySelectorAll('[data-search-term]').forEach((b) => b.addEventListener('click', () => { this.input.value = b.dataset.searchTerm; this.search(); }));
      this.panel.addEventListener('click', (e) => { if (e.target === this.panel) this.close(); });
    }
    reset() { this.results.innerHTML = ''; this.results.hidden = true; this.defaults.hidden = false; }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.reset(); return; }
      this.controller?.abort();
      this.controller = new AbortController();
      this.panel.classList.add('is-loading');
      const url = `${H.routes.predictiveSearch || '/search/suggest'}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,query&resources[limit]=6&resources[limit_scope]=each&section_id=predictive-search`;
      try {
        const res = await fetch(url, { signal: this.controller.signal });
        const html = await res.text();
        const fresh = parseHTML(html).querySelector('[data-search-results]');
        this.results.innerHTML = fresh ? fresh.innerHTML : '';
        this.results.hidden = false;
        this.defaults.hidden = true;
      } catch (err) { if (err.name !== 'AbortError') console.warn(err); }
      finally { this.panel.classList.remove('is-loading'); }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------------
     Filtres & tri (collection) — rendu par Section Rendering API
     ------------------------------------------------------------------ */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.sectionId = this.dataset.sectionId;
      this.form?.addEventListener('change', debounce(() => this.apply(), 120));
      this.form?.addEventListener('submit', (e) => { e.preventDefault(); this.apply(); });
      document.querySelector('[data-sort-select]')?.addEventListener('change', (e) => this.apply({ sort_by: e.target.value }));
      this.addEventListener('click', (e) => {
        const clear = e.target.closest('[data-facet-clear]');
        if (clear) { e.preventDefault(); this.load(clear.href); }
      });
      window.addEventListener('popstate', () => this.load(window.location.href, false));
      // Drawer mobile
      this.facets = this.querySelector('.facets');
      document.querySelectorAll('[data-open-facets]').forEach((b) => b.addEventListener('click', () => this.openDrawer(b)));
      this.querySelectorAll('[data-close-facets]').forEach((b) => b.addEventListener('click', () => this.closeDrawer()));
      this.panel = this.facets;
    }
    open(opener) { this.openDrawer(opener); }
    openDrawer(opener) { this.opener = opener; this.facets.classList.add('is-open'); Overlay.show(this); this.releaseFocus = trapFocus(this.facets); }
    closeDrawer() { this.facets.classList.remove('is-open'); Overlay.hide(this); this.releaseFocus?.(); this.opener?.focus?.(); }
    close() { this.closeDrawer(); }
    apply(extra = {}) {
      const params = new URLSearchParams(new FormData(this.form));
      const current = new URL(window.location.href);
      const sort = extra.sort_by || current.searchParams.get('sort_by');
      if (sort) params.set('sort_by', sort);
      params.delete('page');
      // Nettoyage des valeurs vides (prix)
      [...params.keys()].forEach((k) => { if (params.get(k) === '') params.delete(k); });
      this.load(`${current.pathname}?${params.toString()}`);
    }
    async load(url, push = true) {
      const grid = document.querySelector('[data-product-grid]');
      grid?.classList.add('is-loading');
      this.controller?.abort();
      this.controller = new AbortController();
      const sep = url.includes('?') ? '&' : '?';
      try {
        const res = await fetch(`${url}${sep}section_id=${this.sectionId}`, { signal: this.controller.signal });
        const html = await res.text();
        const doc = parseHTML(html);
        ['[data-product-grid]', '[data-facets-scroll]', '[data-facets-active]', '[data-product-count]', '[data-pagination]', '[data-facets-count]'].forEach((sel) => {
          const fresh = doc.querySelector(sel); const cur = document.querySelector(sel);
          if (fresh && cur) cur.innerHTML = fresh.innerHTML;
        });
        if (push) window.history.pushState({}, '', url);
        Reveal.init(grid);
        if (window.matchMedia('(max-width: 989px)').matches) { /* garde le drawer ouvert pour enchaîner les filtres */ }
      } catch (err) { if (err.name !== 'AbortError') toast(H.strings.error, 'error'); }
      finally { grid?.classList.remove('is-loading'); }
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* Charger plus */
  class LoadMore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.button?.addEventListener('click', () => this.load());
    }
    async load() {
      const next = this.dataset.nextUrl;
      if (!next) return;
      this.button.classList.add('is-loading');
      try {
        const sep = next.includes('?') ? '&' : '?';
        const res = await fetch(`${next}${sep}section_id=${this.dataset.sectionId}`);
        const doc = parseHTML(await res.text());
        const grid = document.querySelector('[data-product-grid]');
        doc.querySelectorAll('[data-product-grid] > *').forEach((el) => grid.appendChild(el));
        const freshLoad = doc.querySelector('load-more');
        if (freshLoad?.dataset.nextUrl) { this.dataset.nextUrl = freshLoad.dataset.nextUrl; this.querySelector('[data-load-more-progress]').style.width = freshLoad.querySelector('[data-load-more-progress]').style.width; this.querySelector('[data-load-more-count]').textContent = freshLoad.querySelector('[data-load-more-count]').textContent; }
        else this.remove();
        Reveal.init(grid);
      } catch (err) { toast(H.strings.error, 'error'); }
      finally { this.button?.classList.remove('is-loading'); }
    }
  }
  customElements.define('load-more', LoadMore);

  /* ------------------------------------------------------------------
     Shop the room — hotspots
     ------------------------------------------------------------------ */
  class ShopTheRoom extends HTMLElement {
    connectedCallback() {
      this.hotspots = $$('.hotspot', this);
      this.items = $$('[data-room-item]', this);
      this.hotspots.forEach((h) => {
        const btn = h.querySelector('.hotspot__btn');
        btn.addEventListener('click', (e) => { e.stopPropagation(); this.toggle(h); });
        h.addEventListener('mouseenter', () => this.highlight(h.dataset.hotspotId, true));
        h.addEventListener('mouseleave', () => this.highlight(h.dataset.hotspotId, false));
        this.positionCard(h);
      });
      this.items.forEach((item) => {
        item.addEventListener('mouseenter', () => this.hotspots.find((h) => h.dataset.hotspotId === item.dataset.roomItem)?.classList.add('is-open'));
        item.addEventListener('mouseleave', () => this.hotspots.find((h) => h.dataset.hotspotId === item.dataset.roomItem)?.classList.remove('is-open'));
      });
      document.addEventListener('click', () => this.closeAll());
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeAll(); });
    }
    positionCard(h) {
      const x = parseFloat(h.style.left); const y = parseFloat(h.style.top);
      if (x < 22) h.classList.add('hotspot--left');
      if (x > 78) h.classList.add('hotspot--right');
      if (y > 65) h.classList.add('hotspot--flip');
    }
    toggle(h) { const open = h.classList.contains('is-open'); this.closeAll(); if (!open) { h.classList.add('is-open'); h.querySelector('.hotspot__btn').setAttribute('aria-expanded', 'true'); } }
    closeAll() { this.hotspots.forEach((h) => { h.classList.remove('is-open'); h.querySelector('.hotspot__btn').setAttribute('aria-expanded', 'false'); }); }
    highlight(id, on) { this.items.forEach((i) => i.classList.toggle('is-active', on && i.dataset.roomItem === id)); }
  }
  customElements.define('shop-the-room', ShopTheRoom);

  /* ------------------------------------------------------------------
     Carrousel horizontal (boutons prev/next)
     ------------------------------------------------------------------ */
  class CarouselNav extends HTMLElement {
    connectedCallback() {
      this.track = document.getElementById(this.dataset.target);
      if (!this.track) return;
      this.prev = this.querySelector('[data-prev]'); this.next = this.querySelector('[data-next]');
      this.prev?.addEventListener('click', () => this.scroll(-1));
      this.next?.addEventListener('click', () => this.scroll(1));
      this.track.addEventListener('scroll', debounce(() => this.update(), 80), { passive: true });
      this.update();
    }
    scroll(dir) { const w = this.track.firstElementChild?.getBoundingClientRect().width || 300; this.track.scrollBy({ left: dir * (w + 16) * 2, behavior: 'smooth' }); }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      this.prev?.toggleAttribute('disabled', this.track.scrollLeft <= 2);
      this.next?.toggleAttribute('disabled', this.track.scrollLeft >= max);
    }
  }
  customElements.define('carousel-nav', CarouselNav);

  /* ------------------------------------------------------------------
     Recommandations produits
     ------------------------------------------------------------------ */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const load = async () => {
        const base = `${H.routes.recommendations || '/recommendations/products'}?section_id=${this.dataset.sectionId}&product_id=${this.dataset.productId}&limit=${this.dataset.limit || 4}`;
        for (const intent of [this.dataset.intent || 'complementary', 'related']) {
          try {
            const res = await fetch(`${base}&intent=${intent}`);
            const doc = parseHTML(await res.text());
            const fresh = doc.querySelector('product-recommendations');
            if (fresh && fresh.querySelector('.card')) { this.innerHTML = fresh.innerHTML; if (intent === 'related') { const t = this.querySelector('[data-reco-title]'); if (t && t.dataset.relatedTitle) t.textContent = t.dataset.relatedTitle; } Reveal.init(this); return; }
          } catch (e) { /* silencieux */ }
        }
        this.remove();
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => { if (entries[0].isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '300px' });
        io.observe(this);
      } else load();
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ------------------------------------------------------------------
     Apparition au scroll
     ------------------------------------------------------------------ */
  const Reveal = {
    io: null,
    init(root = document) {
      if (!document.body.classList.contains('has-animations') || !('IntersectionObserver' in window)) {
        $$('[data-reveal], [data-reveal-group]', root).forEach((el) => el.classList.add('is-revealed'));
        return;
      }
      this.io = this.io || new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); this.io.unobserve(entry.target); } });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
      $$('[data-reveal]:not(.is-revealed), [data-reveal-group]:not(.is-revealed)', root).forEach((el) => this.io.observe(el));
    }
  };

  /* ------------------------------------------------------------------
     Onglets légers
     ------------------------------------------------------------------ */
  class HomyTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = $$('[role="tab"]', this); this.panels = $$('[role="tabpanel"]', this);
      this.tabs.forEach((t, i) => t.addEventListener('click', () => this.select(i)));
      this.select(0);
    }
    select(i) {
      this.tabs.forEach((t, k) => { t.setAttribute('aria-selected', k === i ? 'true' : 'false'); t.tabIndex = k === i ? 0 : -1; });
      this.panels.forEach((p, k) => { p.hidden = k !== i; });
    }
  }
  customElements.define('homy-tabs', HomyTabs);

  /* ------------------------------------------------------------------
     Localisation (formulaire pays/langue du footer)
     ------------------------------------------------------------------ */
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-localization-select]');
    if (sel) sel.closest('form')?.submit();
  });

  /* ------------------------------------------------------------------
     Page panier (mêmes interactions que le drawer)
     ------------------------------------------------------------------ */
  class CartPage extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) { e.preventDefault(); this.changeItem(remove.closest('[data-line]'), 0); }
      });
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cart-quantity]');
        if (input) this.changeItem(input.closest('[data-line]'), parseInt(input.value, 10));
      });
    }
    async changeItem(row, quantity) {
      if (!row) return;
      row.classList.add('is-removing');
      try {
        const pageSection = this.dataset.sectionId;
        const payload = { line: row.dataset.line, quantity, sections: `${pageSection},cart-drawer`, sections_url: window.location.pathname };
        const res = await fetch(H.routes.cartChange || '/cart/change.js', { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(payload) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || H.strings.cartError);
        const fresh = parseHTML(data.sections[pageSection] || '').querySelector('[data-cart-page-items]');
        if (fresh) this.querySelector('[data-cart-page-items]').innerHTML = fresh.innerHTML;
        if (data.sections['cart-drawer']) Cart.render(data.sections['cart-drawer']); else Cart.updateCount(data.item_count);
      } catch (err) { row.classList.remove('is-removing'); toast(err.message, 'error'); }
    }
  }
  customElements.define('cart-page', CartPage);

  /* Choix du nombre de colonnes (collection) */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-grid-cols]');
    if (!btn) return;
    const grid = document.querySelector('[data-product-grid]');
    if (!grid) return;
    grid.style.setProperty('--cols', btn.dataset.gridCols);
    btn.parentElement.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
    try { localStorage.setItem('homy:grid-cols', btn.dataset.gridCols); } catch (_) {}
  });
  const restoreGridCols = () => {
    try {
      const cols = localStorage.getItem('homy:grid-cols');
      const grid = document.querySelector('[data-product-grid]');
      if (cols && grid && window.matchMedia('(min-width: 990px)').matches) {
        grid.style.setProperty('--cols', cols);
        document.querySelectorAll('[data-grid-cols]').forEach((b) => b.setAttribute('aria-pressed', b.dataset.gridCols === cols ? 'true' : 'false'));
      }
    } catch (_) {}
  };

  /* ------------------------------------------------------------------
     Init
     ------------------------------------------------------------------ */
  const init = () => { Overlay.init(); Reveal.init(); restoreGridCols(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  // Éditeur de thème Shopify
  document.addEventListener('shopify:section:load', (e) => Reveal.init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const hotspot = e.target.closest?.('.hotspot') || e.target.querySelector?.('.hotspot');
    hotspot?.classList.add('is-open');
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const hotspot = e.target.closest?.('.hotspot') || e.target.querySelector?.('.hotspot');
    hotspot?.classList.remove('is-open');
  });

  window.homy = Object.assign(H, { Cart, toast, formatMoney, Reveal });
})();
